/* eslint-disable no-control-regex */
/* eslint-disable no-useless-escape */
import { TransferScopeEnum } from '@tassat/fred-definitions';
import { transferScopeCodeToEnum } from '@tassat/fred-extended-definitions';
import { AdminRoleId } from '@tassat/ui-definitions';
import { convertToNumberString, currencyAsNumber } from '@tassat/utility';
import moment from 'moment';

export const validateUsdCurrency = (value) => {
    const valueAsNumber = currencyAsNumber(value)
        .value();
    if (!value || value === '$') {
        return 'Required';
    }
    if (!/^\$(\d*,?)+(\.\d{1,2})?$/.test(value)) {
        return 'Invalid amount';
    }
    if (parseFloat(value.substring(1)) < parseFloat('0.01')) {
        return 'Value must be greater than $0.00';
    }
    if (valueAsNumber && (valueAsNumber > 999999999.99)) {
        // Why yes, the maximum value that you may use is $999,999,999.99.
        // There's no way anyone has $1 BILLION DOLLARS!
        return 'Value must be strictly less than $1,000,000,000.00';
    }

    return null;
};

export const validateMultiCurrency = (value, selectedCurrencyDecimalPoint) => {
    const amount = convertToNumberString(value);
    if (!amount) {
        return 'Required';
    }
    if (!new RegExp(`^(\\d*,?)+${selectedCurrencyDecimalPoint && `(\\.\\d{1,${selectedCurrencyDecimalPoint}})`}?$`)
        .test(amount)) {
        return 'Invalid amount';
    }
    if (parseFloat(amount) < parseFloat(`${selectedCurrencyDecimalPoint ? `0.${'0'.repeat(selectedCurrencyDecimalPoint - 1)}1` : '1'}`)) {
        return `Value must be greater than 0${selectedCurrencyDecimalPoint ? `.${'0'.repeat(selectedCurrencyDecimalPoint)}` : ''}`;
    }
    if (parseFloat(amount) > 999999999.99) {
        // Why yes, the maximum value that you may use is $999,999,999.99.
        // There's no way anyone has $1 BILLION DOLLARS!
        return 'Value must be strictly less than 1,000,000,000.00';
    }

    return null;
};

export const validateWalletAddress = (value, accountType: string = 'Wallet') => {
    if (!value) {
        return 'Required';
    }
    if (!/^0x([0-9]|[A-F]|[a-f])+$/.test(value)) {
        return `${accountType} address contains invalid characters`;
    }
    if (value.length !== 42) {
        return `${accountType} address is an invalid length`;
    }

    return null;
};

export const validateCompanyCode = (companyCode, regexExp) => {
    if (!companyCode?.length) {
        return 'Required';
    }

    const parsedRegex = JSON.parse(regexExp);
    const isValidCompanyCode = RegExp(parsedRegex)
        .test(companyCode);

    if (!isValidCompanyCode) {
        return 'Invalid Company Code';
    }

    return null;
};

export const validateCompanyCodeUniqueness = (companyCode, firmList, initialValue) => {
    if (!companyCode || companyCode === initialValue) {
        return null;
    }

    const duplicatedCompanyCodeFirm = firmList.find((firm) => firm.CompanyCode === companyCode);

    return duplicatedCompanyCodeFirm ? `Company code already linked to firm ${duplicatedCompanyCodeFirm.Name}` : null;
};

export const validateEmailUniqueness = (email, users, initialValue = '') => {
    if (!email || email.toLowerCase() === initialValue.toLowerCase()) {
        return null;
    }

    const currentEmails = Object.keys(users)
        .map((key) => {
            const user = users[key];

            return !user.IsDeleted && user.Email.toLowerCase() !== initialValue.toLowerCase() ?
                users[key].Email.toLowerCase()
                : null;
        });

    if (currentEmails.includes(email.toLowerCase())) {
        return 'E-mail is currently in use, please enter a unique email';
    }

    return null;
};

export const validateSsoUserIdUniqueness = (ssoUserId, users, initialValue = '') => {
    if (!ssoUserId || ssoUserId === initialValue) {
        return null;
    }

    const currentSsoUserIds = Object.keys(users)
        .map((key) => {
            const user = users[key];

            return user?.SsoUserId && user.SsoUserId !== initialValue ? user.SsoUserId : null;
        });

    if (currentSsoUserIds.includes(ssoUserId)) {
        return 'SSO UserID is currently in use, please enter a unique SSO UserID';
    }

    return null;
};

export const validateSsoUserIdFormatting = (ssoUserId, required = false) => {
    if (!ssoUserId && !required) {
        return null;
    }
    if (!ssoUserId) {
        return 'Required';
    }

    const re = new RegExp('^[a-zA-Z0-9_!#$%&\'*+\/=?`{|}~^.-]+@[A-Za-z0-9-]+(\\.[A-Za-z0-9]+)*(\\.[A-Za-z]{2,})$');

    const correctlyFormatted = re.test(String(ssoUserId));

    if (!correctlyFormatted) {
        return 'Please enter a valid SSO UserID format';
    }

    return null;
};

export const validateFullNameUniqueness = (fullName, users, firmId) => {
    if (!fullName || !users || !firmId) {
        return null;
    }

    const firmUserNames = Object.keys(users)
        .map((key) => {
            const user = users[key];

            return !user.IsDeleted && user.FirmId === firmId.toString() ? `${user.FirstName.toLowerCase()} ${user.LastName.toLowerCase()}` : null;
        });

    if (firmUserNames.includes(fullName.toLowerCase())) {
        return 'A user with this first and last name already exists, please enter a unique name';
    }

    return null;
};

export const validateEmailFormatting = (email, required = false) => {
    if (!email && !required) {
        return null;
    }
    if (!email) {
        return 'Required';
    }

    const re = new RegExp('^[a-zA-Z0-9_!#$%&\'*+\/=?`{|}~^.-]+@[A-Za-z0-9-]+(\\.[A-Za-z0-9]+)*(\\.[A-Za-z]{2,})$');

    const correctlyFormatted = re.test(String(email)
        .toLowerCase());

    if (!correctlyFormatted) {
        return 'Please enter a valid e-mail address';
    }

    if (email.length > 64) {
        return 'E-mail address exceeds number of characters';
    }

    return null;
};

export const validateRequired = (value) => (value ? null : 'Required');

const newFirmUniquenessMessages = {
    Name: 'A firm with this name already exists, please enter a unique name',
    Account: 'A firm with this account number already exists, please enter a unique account number',
    ShortName: 'A firm with this short name already exists, please enter a unique name',
    Email: 'A firm with this email already exists, please enter a unique email address'
};

export const validateUniqueness = (field, value, objects) => {
    if (!value) {
        return 'Required';
    }

    const lowercase = value.toLowerCase();

    const notUnique = Object.keys(objects)
        .some((key) => objects[key][field].toLowerCase() === lowercase);
    if (notUnique) {
        return newFirmUniquenessMessages[field];
    }

    return null;
};

export const validateDDAsPerAbaUniqueness = (ddas) => {
    if (!ddas) return null;

    const ddaValues = ddas.map((dda) => dda.Account);
    const ddaErrors = ddaValues.reduce((acc, ddaValue, index) => {
        const isRepeated = ddaValues.indexOf(ddaValue) !== index;
        if (isRepeated) {
            return {
                ...acc,
                [index]: { Account: 'Error - Duplicate Entry Exists' }
            };
        }

        return acc;
    }, {});

    return Object.keys(ddaErrors).length > 0 ? ddaErrors : null;
};

export const validateRestictedDDAsRequired = (ddas) => {
    if (!ddas) return null;

    const ddaValues = ddas.map((dda) => dda.Account);
    const required = ddaValues[ddaValues.length - 1];

    if (ddaValues.length > 1 && required === '') {
        return { [ddaValues.length - 1]: { Account: 'Extra DDAs cannot be empty' }};
    }

    return null;
};

export const validateLengthRequirement = (field, length, exact = false) => {
    if (!field) {
        return null;
    }
    if (exact) {
        return field.length === length ? null : `Must be exactly ${length} characters long`;
    }

    return field.length <= length ? null : `Must be ${length} characters long or shorter`;
};

export const validateDDALengthRequirement = (field, lengthObj) => {
    if (!field) {
        return null;
    }
    if (lengthObj.ddaLengthMax === lengthObj.ddaLengthMin) {
        return field.length === lengthObj.ddaLengthMax ? null : `Must be exactly ${lengthObj.ddaLengthMax} characters long`;
    }
    if (field.length < lengthObj.ddaLengthMin) return `Must be ${lengthObj.ddaLengthMin} characters long at least`;

    return null;
};

export const validateAccountNumberFormatting = (
    accountNumber: string,
    fieldName?: string,
    opts?: {
        allowLeadingZeros?: boolean;
        allowAlphanumeric?: boolean;
        allowAlphanumericCapital?: boolean;
    }
) => {
    if (!accountNumber) {
        return `${fieldName ?? 'BD Account Number'} is required`;
    }

    if (opts?.allowLeadingZeros !== true && accountNumber[0] === '0') {
        return `No leading zeroes on ${fieldName ?? 'BD Account Number'}`;
    }

    let re = /^[0-9]+$/;
    if (opts?.allowAlphanumeric) {
        re = /^[a-zA-Z0-9]+$/;
    } else if (opts?.allowAlphanumericCapital) {
        re = /^[A-Z0-9]+$/;
    }
    const correctlyFormatted = re.test(accountNumber);

    if (!correctlyFormatted) {
        if (opts?.allowAlphanumeric) {
            return `${fieldName ?? 'BD Account Number'} can only contain alphanumeric values`;
        }

        if (opts?.allowAlphanumericCapital) {
            return `${fieldName ?? 'BD Account Number'} can only contain capital alphanumeric values`;
        }

        return `${fieldName ?? 'BD Account Number'} can only contain numbers`;
    }

    return null;
};

export const validateDescriptionInput = (description, text) => {
    if (!description) {
        return null;
    }

    if (/[|,"_=+!@#$%^&*;:~/\\\?\<\>\{\}\[\]\(\)].*$/.test(description)) {
        return `${text} cannot contain commas, double quotes or special chars`;
    }

    return null;
};

export const validateNoSpecialCharacters = (description, text = 'Text') => {
    if (!description) {
        return null;
    }

    if (!text.trim()) {
        return 'Field cannot be an empty space';
    }

    if (/^[a-zA-Z0-9\-_=+!@#$%^&*;:~ \/\'\?\<\>\{\}\[\]\(\).]*$/.test(description)) {
        return null;
    }

    return `Invalid ${text}`;
};

// TODO: The validation of strings should be rewritten to take in values.
export const firmNameAndWalletTextValidation = (text) => {
    if (!text) {
        return null;
    }

    if (!text.trim()) {
        return 'Field cannot be an empty space';
    }

    if (/^[a-zA-Z0-9\-_=+!@,#$%^&*;:~ \/\'\?\{\}\[\]\(\).]*$/.test(text)) {
        return null;
    }

    return 'Invalid Text';
};

export const validateNoDelimiters = (text) => {
    if (/[,|\\\/]+/g.test(text)) {
        return 'Value cannot contain a delimiting characters (",", "|", "\\", "/")';
    }

    return null;
};

export const validateACGchars = (text) => {
    if (/[+,#<>|"\\\/]+/g.test(text)) {
        return 'ACG Name cannot contain a delimiting characters (#,<,>,|,\\,/,",+, and , )';
    }

    return null;
};

// Same goal as ValidateDescriptionInput but less checks
export const validateDescriptionFields = (description, text) => {
    if (/[,\"\/'\|\\<>]+/g.test(description)) {
        return `${text} cannot contain commas, double quotes or special chars`;
    }

    return null;
};

export const validatePcgUserMappedPcgNumbers = (userValues) => {
    if (userValues.AdminRoleId && userValues.AdminRoleId.value === AdminRoleId.PCG_ADMIN) {
        if (!userValues.PCGMapping || userValues.PCGMapping.length === 0) {
            return 'Must select at least 1 PCG Number for PCG User';
        }
        if (userValues.PCGMapping && userValues.PCGMapping.length > 5) {
            return 'Users may only be mapped to a maximum of 5 PCG Numbers';
        }
    }

    return null;
};

export const validatePcgAccountMapping = (pcgNumbers, groupingName = 'PCG', accountName = 'Account') => {
    if (!pcgNumbers || pcgNumbers.length === 0) {
        return `Must select at least 1 ${groupingName} Number for this account`;
    }
    if (pcgNumbers.length > 3) {
        return `${accountName} can only have maximum 3 ${groupingName} Numbers`;
    }

    return null;
};

export const validateDate = (dateValue) => {
    const date = moment(dateValue);
    if (!date.isValid()) {
        return 'Please enter a valid date';
    }

    return null;
};

export const validatePhonenumber = (phoneValue) => {
    if (!phoneValue) return null;

    if (!/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im.test(phoneValue)) {
        return 'Please enter a valid phone number';
    }

    return null;
};

export const validateAlphaNumeric = (text) => {
    if (!(/^[a-zA-Z0-9]*$/.test(text))) {
        return 'Value must be alphanumeric, (a-z, A-Z, 0-9)';
    }

    return null;
};

export const validateNumbersOnly = (value, msg?: string) => {
    if (/\D/.test(value)) {
        return msg || 'Text can only contain numbers';
    }

    return null;
};

export const validateNoLeadingZero = (text) => {
    if (/^0+/.test(text)) {
        return 'Value cannot start with a leading zero';
    }

    return null;
};

export const validateNoWhitespace = (text) => {
    if (/\s+/g.test(text)) {
        return 'Value cannot have whitespace';
    }

    return null;
};

export const validateRequiredStrict = (value) => {
    let requiredValue = value;
    if (typeof value === 'string') requiredValue = value.trim();

    return requiredValue ? null : 'Required';
};

export const validateBasicURL = (text) => {
    if (!/^[a-zA-Z0-9\-_/.]*$/.test(text)) {
        return 'Value must only contain URL allowed characters';
    }

    return null;
};

export const validateNoHtmlTagCharacters = (value) => {
    if (typeof value === 'string') {
        if (/[<>]/g.test(value)) {
            return 'Characters < and > are not allowed';
        }

        return null;
    }

    return 'Bad value';
};

export const validateNoExcelFormulaStartingCharacters = (value) => {
    if (typeof value === 'string') {
        const firstChar = value[0];
        if ([ '+', '@', '=', '-' ].includes(firstChar)) {
            return 'First character cannot be +, @, =, -';
        }

        return null;
    }

    return 'Bad value';
};

export const validateNoNonPrintable = (value) => {
    if (
        /* Control code Unicode range taken from non-printable (red)
        characters from https://altcodeunicode.com/
        */
        /[\u0000-\u001F]/g.test(value) ||
        /* Also prevent emojis
        Unicode range taken from https://mothereff.in/regexpu
        and converting /\p{Extended_Pictographic}/u to es5 regexp
        */
        /(?:[\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u231A\u231B\u2328\u2388\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2605\u2607-\u2612\u2614-\u2685\u2690-\u2705\u2708-\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763-\u2767\u2795-\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]|\uD83C[\uDC00-\uDCFF\uDD0D-\uDD0F\uDD2F\uDD6C-\uDD71\uDD7E\uDD7F\uDD8E\uDD91-\uDD9A\uDDAD-\uDDE5\uDE01-\uDE0F\uDE1A\uDE2F\uDE32-\uDE3A\uDE3C-\uDE3F\uDE49-\uDFFA]|\uD83D[\uDC00-\uDD3D\uDD46-\uDE4F\uDE80-\uDEFF\uDF74-\uDF7F\uDFD5-\uDFFF]|\uD83E[\uDC0C-\uDC0F\uDC48-\uDC4F\uDC5A-\uDC5F\uDC88-\uDC8F\uDCAE-\uDCFF\uDD0C-\uDD3A\uDD3C-\uDD45\uDD47-\uDEFF]|\uD83F[\uDC00-\uDFFD])/.test(value)
    ) {
        return 'Text can only contain printable characters';
    }

    return null;
};

export const validateRichText = (value) => {
    // Total character count cannot exceed 255
    if (value.length > 255) {
        return 'Total length of message including styling cannot exceed 255 characters';
    }

    const isStylyed = /[**|_]/gm.test(value);
    const cleanedValue = value.replace(/[**|_]/gm, '');

    if (isStylyed && cleanedValue.length > 230) {
        return 'Message cannot be greater than 230 characters';
    }
    const nonPrintableError = validateNoNonPrintable(cleanedValue);
    if (/[\"'\|\\<>]+/g.test(value)) {
        return 'Rich text cannot contain quotes or special chars';
    }
    if (nonPrintableError) {
        return nonPrintableError;
    }

    return null;
};

export const validateSingleDoubleQuotes = (description, text) => {
    if (/['"]/gm.test(description)) {
        return `${text} cannot contain single or double quotes`;
    }

    return null;
};

export const validateNoDelimitersMinusComma = (text, field) => {
    if (/[|\\\/]+/g.test(text)) {
        return `${field || 'Value'} cannot contain delimiting characters ("|", "\\", "/")`;
    }

    return null;
};

export const validateFirmAssetsRequired = (assetMapping) => {
    if (!assetMapping || assetMapping.length === 0) {
        return 'Firms must have at least 1 asset permission';
    }

    return null;
};

const PENDING_POSITION_COUNT_FIELDS = [
    'PendingApprovalWithdrawalCount',
    'PendingApprovalSendCount',
    'PendingApprovalBuyCount',
    'PendingApprovalSellCount',
    'PendingApprovalSubscriptionCount',
    'PendingApprovalRedemptionCount',
    'PendingWithdrawalCount',
    'PendingBuyCount',
    'PendingSellCount',
    'PendingSubscriptionCount',
    'PendingRedemptionCount'
];

export const validateAssetRemoval = (removedAssets, firmEoaAccounts) => {
    const blockedAssetTickers = removedAssets
        .filter(({ assetId }) => firmEoaAccounts?.some((account) => account.walletStats?.some((stats) => {
            if (stats.AssetId !== assetId) {
                return false;
            }

            const hasPositiveBalance = parseFloat(stats.CurrentPosition) > 0 ||
                parseFloat(stats.TotalPledgeAmount) > 0 ||
                parseFloat(stats.TotalPledgeReceived) > 0;
            const hasPendingTransaction = PENDING_POSITION_COUNT_FIELDS.some((field) => parseFloat(stats[field]) > 0);

            return hasPositiveBalance || hasPendingTransaction;
        })))
        .map(({ ticker }) => ticker);

    if (blockedAssetTickers.length > 0) {
        const verb = blockedAssetTickers.length > 1 ? 'have' : 'has';

        return `${blockedAssetTickers.join(', ')} ${verb} an active balance or pending transactions and can't be removed.`;
    }

    return null;
};

export const validateAccountCurrencyRequiredSingleValue = (currencyMapping) => {
    if (!currencyMapping || currencyMapping.value === '') {
        return 'Must select currency for this account';
    }
};

export const validateUsernameUniqueness = (username, users, initialValue) => {
    if (!username || username === initialValue) {
        return null;
    }

    const currentUsernames = Object.keys(users)
        .map((key) => {
            const user = users[key];

            return user?.Username && user.Username !== initialValue ? user.Username : null;
        });

    if (currentUsernames.includes(username)) {
        return 'username is currently in use, please enter a unique username';
    }

    return null;
};

export const validateUsernameFormatting = (username, isRequired = false) => {
    if (!username && !isRequired) {
        return null;
    }

    if (!username) {
        return 'Required';
    }

    if (username.length < 6) {
        return 'Username must be minimum of 6 characters.';
    }

    if (/[<>&"'\s$%!#?§,;:*~\/\\|^=\[\]{}()`\x00-\x1F\x7F]/.test(username)) {
        return 'Username cannot have invalid characters.';
    }
};

export const validateBeneAddress2Formatting = (beneAddress2) => {
    // FRED-4719: State and Zip Fields are now optional and match Zip Code Regex from validatePostalCodeFormattingNoSpaces
    if (/^([\w ]+, ){1}([\w ]*, ){1}[A-Za-z0-9- ]*$/.test(beneAddress2)){
        return null;
    }

    return 'Invalid Format';
};

export const validatePostalCodeFormatting = (postalCode) => {
    if (/^([A-Za-z0-9- ]){0,10}$/.test(postalCode)){
        return null;
    }

    return 'Please enter a valid Zip/Postal Code';
    // ^ = Start of the string.
    // \d{5} = Match 5 digits (for condition 1, 2, 3)
    // (?:…) = Grouping
    // [-\s] = Match a space (for condition 3) or a hyphen (for condition 2)
    // \d{4} = Match 4 digits (for condition 2, 3)
    // …? = The pattern before it is optional (for condition 1)
    // $ = End of the string.
};

export const validatePostalCodeFormattingNoSpaces = (postalCode) => {
    // FRED-4719: Allow 10 alphanumeric characters and dashes
    if (/^([A-Za-z0-9-]){0,10}$/.test(postalCode)){
        return null;
    }

    return 'Please enter a valid Zip/Postal Code';
};

export const textFieldCharacterWhitelist = (text, opts?: { excludeComma?: boolean }) => {
    if (!text) {
        return null;
    }

    if (!text.trim()) {
        return 'Field cannot be an empty space';
    }

    if (opts?.excludeComma && /^[a-zA-Z0-9\-_=+!@#$%^&*;:~ \/\\|\'\?\{\}\[\]\(\).`]*$/.test(text)) {
        return null;
    }

    if (!opts?.excludeComma && /^[a-zA-Z0-9\-_=+!@,#$%^&*;:~ \/\\|\'\?\{\}\[\]\(\).`]*$/.test(text)) {
        return null;
    }

    return 'Invalid Text';
};

export const validateNoCommas = (text) => {
    if (text.includes(',')) {
        return 'Field cannot contain a comma';
    }

    return null;
};

export const validateCapitalLetters = (text) => {
    if (/^[A-Z]*$/.test(text)) {
        return null;
    }

    return 'Field can only contain capital letters (A-Z)';
};

export const validateNoPoBox = (text: string): null | string => {
    if (!text) return null;

    const poRegExp = new RegExp(/\b(P\.?\s*O\.?\s*Box|Post\s*Office\s*Box)\b/i);

    if (poRegExp.test(text)) {
        return 'PO Box not allowed';
    }

    return null;
};

export const isTransferAllowedForScope = (scope: TransferScopeEnum | number | undefined, sameFirm: boolean): boolean => {
    const transferScope = transferScopeCodeToEnum(scope);

    if (transferScope === TransferScopeEnum.ALL) {
        return true;
    }

    if (transferScope === TransferScopeEnum.INTER_FIRM) {
        return !sameFirm;
    }

    // TransferScopeEnum.INTRA_FIRM, or missing/unrecognized scope, defaults to firm-restricted
    return sameFirm;
};